// src/pages/admin/Properties.jsx
import { useState } from 'react';
import { FaHome, FaSearch, FaMapMarkerAlt, FaPhone, FaEnvelope } from 'react-icons/fa';
import AdminLayout from '../../components/dashboard/admin/AdminLayout';
import useCustomers from '../../hooks/useCustomers';

const fmtDate = (date) =>
  date
    ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date)
    : '—';

const AdminProperties = () => {
  const { customers, loading } = useCustomers();
  const [search, setSearch] = useState('');

  // Only customers with a non-empty address
  const properties = customers.filter((c) => c.address?.trim());

  const filtered = properties.filter((c) => {
    const q = search.toLowerCase();
    return (
      !q ||
      c.address.toLowerCase().includes(q) ||
      `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q)
    );
  });

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Properties</h1>
            <p className="text-sm text-gray-500 mt-1">
              Customers with a saved address — edit addresses in the Customers page.
            </p>
          </div>
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm w-full sm:w-72">
            <FaSearch className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              type="text"
              placeholder="Search by name or address..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400"
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto" />
            <p className="mt-4 text-sm text-gray-500">Loading properties...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FaHome className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-base font-medium text-gray-700">
              {search ? 'No properties match your search' : 'No customer addresses on file'}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {!search && 'Add an address to a customer profile to see it here.'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    {['Customer', 'Address', 'Contact', 'Added'].map((h) => (
                      <th
                        key={h}
                        className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((customer) => (
                    <tr key={customer.id} className="hover:bg-gray-50 transition-colors">
                      {/* Customer */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-semibold text-primary-700">
                              {customer.firstName?.[0]}{customer.lastName?.[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {customer.firstName} {customer.lastName}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Address */}
                      <td className="px-6 py-4">
                        <div className="flex items-start gap-2">
                          <FaMapMarkerAlt className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{customer.address}</span>
                        </div>
                      </td>

                      {/* Contact */}
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {customer.email && (
                            <a
                              href={`mailto:${customer.email}`}
                              className="flex items-center gap-1.5 text-gray-500 hover:text-primary-600 transition-colors"
                            >
                              <FaEnvelope className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="text-xs">{customer.email}</span>
                            </a>
                          )}
                          {customer.phone && (
                            <a
                              href={`tel:${customer.phone}`}
                              className="flex items-center gap-1.5 text-gray-500 hover:text-primary-600 transition-colors"
                            >
                              <FaPhone className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="text-xs">{customer.phone}</span>
                            </a>
                          )}
                        </div>
                      </td>

                      {/* Date */}
                      <td className="px-6 py-4 text-gray-400 text-xs">
                        {fmtDate(customer.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filtered.map((customer) => (
                <div key={customer.id} className="p-4 space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-primary-700">
                        {customer.firstName?.[0]}{customer.lastName?.[0]}
                      </span>
                    </div>
                    <p className="font-medium text-gray-900">
                      {customer.firstName} {customer.lastName}
                    </p>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-600">
                    <FaMapMarkerAlt className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <span>{customer.address}</span>
                  </div>
                  {customer.email && (
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <FaEnvelope className="w-3.5 h-3.5" />
                      <a href={`mailto:${customer.email}`} className="hover:text-primary-600">{customer.email}</a>
                    </div>
                  )}
                  {customer.phone && (
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <FaPhone className="w-3.5 h-3.5" />
                      <a href={`tel:${customer.phone}`} className="hover:text-primary-600">{customer.phone}</a>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-500">
              {filtered.length} of {properties.length} propert{properties.length !== 1 ? 'ies' : 'y'} shown
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminProperties;